import { createFileRoute, Link } from '@tanstack/react-router';
import { Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { ChevronRight, Plus, Search, UserPlus, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  PageContainer,
  PageHeader,
  Pill,
  Section,
  Table,
  type TableColumn,
} from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import { useCohorts, useCreateCohort } from '../hooks/useCohorts';
import { useCourses } from '../hooks/useCourses';
import type { CohortStatus, CohortSummary } from '../api/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/admin/cohorts/')({
  component: AdminCohorts,
});

const STATUS_VARIANT: Record<CohortStatus, 'success' | 'info' | 'warn' | 'danger'> = {
  active: 'success',
  paused: 'warn',
  archived: 'danger',
  draft: 'info',
};

export function AdminCohorts({ basePath = '/admin' }: { basePath?: string } = {}) {
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const role = useAuthStore(selectRole);
  const canManage = role === 'org_admin' || role === 'superadmin';
  const { data, isPending, isError, error, refetch } = useCohorts();

  const filtered = useMemo(() => {
    if (!data) return [] as ReadonlyArray<CohortSummary>;
    const lower = q.trim().toLowerCase();
    if (!lower) return data;
    return data.filter(
      (c) =>
        c.name.toLowerCase().includes(lower) ||
        c.courseTitle.toLowerCase().includes(lower) ||
        c.status.includes(lower),
    );
  }, [data, q]);

  const columns: ReadonlyArray<TableColumn<CohortSummary>> = useMemo(
    () => [
      {
        key: 'name',
        header: 'Cohort',
        cell: (c) => (
          <Stack gap={2}>
            <Text size="sm" c="rgb(var(--c-txt-bright))">
              {c.name}
            </Text>
            <Text size="xs" c="dimmed" ff="JetBrains Mono">
              {c.id}
            </Text>
          </Stack>
        ),
      },
      {
        key: 'course',
        header: 'Course',
        cell: (c) => (
          <Text size="sm" c="rgb(var(--c-txt-bright))">
            {c.courseTitle}
          </Text>
        ),
      },
      {
        key: 'trainees',
        header: 'Trainees',
        cell: (c) => (
          <Pill variant={c.traineeCount === 0 ? 'warn' : 'info'} size="xs">
            {c.traineeCount}
          </Pill>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        cell: (c) => (
          <Badge variant={STATUS_VARIANT[c.status]} size="xs">
            {c.status}
          </Badge>
        ),
      },
      {
        key: 'created',
        header: 'Created',
        cell: (c) => (
          <Text size="xs" c="dimmed" ff="JetBrains Mono">
            {new Date(c.createdAt).toLocaleDateString()}
          </Text>
        ),
      },
      {
        key: 'actions',
        header: '',
        cell: (c) => (
          <Group gap={6} justify="flex-end">
            <Link {...({ to: `${basePath}/cohorts/$id/enroll`, params: { id: c.id } } as any)} style={{ textDecoration: 'none' }}>
              <Button variant="ghost" size="sm" leftIcon={<UserPlus size={12} />}>
                Enroll
              </Button>
            </Link>
            <ChevronRight size={14} color="#5a5a64" aria-hidden />
          </Group>
        ),
      },
    ],
    [],
  );

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Users}
        title="Cohorts"
        description="Training cohorts and their enrolled trainees."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Admin', to: '/admin' }, { label: 'Cohorts' }]}
        actions={
          canManage ? (
            <Button variant="primary" leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
              Create cohort
            </Button>
          ) : undefined
        }
      />

      <Section
        title="All cohorts"
        description="Group trainees into cohorts for enrollment, scheduling, and instructor reporting."
        action={
          <TextInput
            leftSection={<Search size={14} />}
            placeholder="Filter by name, course, or status"
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            style={{ maxWidth: 320 }}
            aria-label="Filter cohorts"
          />
        }
      >
        {isError ? (
          <ErrorState
            title="Could not load cohorts"
            message={(error as Error)?.message ?? 'Unknown error'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <LoadingState rows={4} height={32} />
        ) : filtered.length === 0 ? (
          renderEmpty(q, canManage, () => setQ(''), () => setCreateOpen(true))
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="show">
            <motion.div variants={cardItem}>
              <Card>
                <Stack gap="md">
                  <Group gap="xs">
                    <Users size={16} />
                    <Text fw={600} size="sm">
                      {filtered.length} cohort{filtered.length === 1 ? '' : 's'}
                    </Text>
                  </Group>
                  <Table columns={columns} rows={filtered} rowKey={(c) => c.id} />
                </Stack>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </Section>

      <CreateCohortModal opened={createOpen} onClose={() => setCreateOpen(false)} />
    </PageContainer>
  );
}

function renderEmpty(q: string, canManage: boolean, clearFilter: () => void, openCreate: () => void) {
  if (q) {
    return (
      <EmptyState
        illustration={<Users size={48} />}
        title="No cohorts match your filter"
        description="Try clearing the search box to see all cohorts."
        cta={{ label: 'Clear filter', onClick: clearFilter, variant: 'secondary' }}
      />
    );
  }
  if (canManage) {
    return (
      <EmptyState
        illustration={<Users size={48} />}
        title="No cohorts yet"
        description="Create the first cohort to start enrolling trainees in courses."
        cta={{ label: 'Create cohort', onClick: openCreate }}
      />
    );
  }
  return (
    <EmptyState
      illustration={<Users size={48} />}
      title="No cohorts yet"
      description="No cohorts have been created in your tenant."
    />
  );
}

interface CreateCohortModalProps {
  opened: boolean;
  onClose: () => void;
}

function CreateCohortModal({ opened, onClose }: CreateCohortModalProps) {
  const { data: courses } = useCourses();
  const create = useCreateCohort();
  const [name, setName] = useState('');
  const [courseId, setCourseId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleClose = () => {
    create.reset();
    setName('');
    setCourseId(null);
    setFormError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setFormError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setFormError('Name is required.');
      return;
    }
    if (!courseId) {
      setFormError('Pick a course to attach this cohort to.');
      return;
    }
    try {
      await create.mutateAsync({ name: trimmed, courseId });
      handleClose();
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const courseOptions = useMemo(
    () => (courses ?? []).map((c) => ({ value: c.id, label: c.title })),
    [courses],
  );

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <span>
          <Users size={14} style={{ marginRight: 6 }} /> New cohort
        </span>
      }
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={create.isPending} leftIcon={<Plus size={14} />}>
            Create
          </Button>
        </>
      }
    >
      <Stack gap="sm">
        <TextInput
          label="Cohort name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder="e.g. SOC L1 — 2026 Q2 intake"
          required
          aria-label="Cohort name"
        />
        <Select
          label="Course"
          data={courseOptions}
          value={courseId}
          onChange={setCourseId}
          placeholder={courseOptions.length === 0 ? 'Loading courses…' : 'Select a course'}
          searchable
          nothingFoundMessage="No matching course"
          required
          aria-label="Course"
        />
        {formError ? (
          <Text size="xs" c="red">
            {formError}
          </Text>
        ) : null}
      </Stack>
    </Modal>
  );
}
